import { translations } from './translations';
import globalStyles from '../scss/global.scss';
import chroma from 'chroma-js';
import cssVars from 'css-vars-ponyfill';

/* * * * * * *
 * VALIDATE  *
 * * * * * * */
export const checkValidity = (value, rules) => {
    if (!rules || (!rules.required && !value.length)) {
        return true;
    }

    const availableRules = [
        {
            name: 'required',
            test: (val) => val.toString().trim().length
        },
        {
            name: 'minLength',
            test: (val) => val.length >= rules.minLength
        },
        {
            name: 'maxLength',
            test: (val) => val.length <= rules.maxLength
        },
        {
            name: 'isEmail',
            test: (val) => {
                // const pattern = /[a-z0-9!#$%&'*+\/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+\/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;
                const pattern = /^([\w-]+(?:\.[\w-]+)*)@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$/i;
                return pattern.test(val);
            }
        },
        {
            name: 'isUrl',
            test: (val) => {
                const pattern = '^(?!mailto:)(?:(?:http|https|ftp)://)(?:\\S+(?::\\S*)?@)?(?:(?:(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}(?:\\.(?:[0-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))|(?:(?:[a-z\\u00a1-\\uffff0-9]+-?)*[a-z\\u00a1-\\uffff0-9]+)(?:\\.(?:[a-z\\u00a1-\\uffff0-9]+-?)*[a-z\\u00a1-\\uffff0-9]+)*(?:\\.(?:[a-z\\u00a1-\\uffff]{2,})))|localhost)(?::\\d{2,5})?(?:(/|\\?|#)[^\\s]*)?$';
                return pattern.test(val);
            }
        },
        {
            name: 'isNumeric',
            test: (val) => {
                const pattern = /^\d+$/;
                return pattern.test(val);
            }
        },
        {
            name: 'isPassword',
            test: (val) => {
                const pattern = /^.*(?=.{6,})(?=.*[a-zA-Z])(?=.*\d)(?=.*[!&$%&? "]).*$/;
                return pattern.test(val);
            }
        },
        {
            name: 'isDutchAddress',
            test: (val) => {
                const pattern = /^([1-9][e][\s])*([a-zA-Z]+(([\.][\s])|([\s]))?)+[1-9][0-9]*(([-][1-9][0-9]*)|([\s]?[a-zA-Z]+))?$/i;
                return pattern.test(val);
            }
        },
        {
            name: 'isDutchPostcode',
            test: (val) => {
                const pattern = /^[1-9][0-9]{3}[\s]?[A-Za-z]{2}$/i;
                return pattern.test(val);
            }
        },
        {
            name: 'isDutchMobileNumber',
            test: (val) => {
                const pattern_vast = /^(((0)[1-9]{2}[0-9][-]?[1-9][0-9]{5})|((\\+31|0|0031)[1-9][0-9][-]?[1-9][0-9]{6}))$/;
                const pattern_mobiel = /^(((\\+31|0|0031)6){1}[1-9]{1}[0-9]{7})$/i;
                return (pattern_vast.test(val) || pattern_mobiel.test(val));
            }
        },
        {
            name: 'isDutchBankNumber',
            test: (val) => {
                const pattern = /^[a-zA-Z]{2}[0-9]{2}[a-zA-Z0-9]{4}[0-9]{7}([a-zA-Z0-9]?){0,16}$/i;
                return pattern.test(val);
            }
        },
        {
            name: 'custom',
            test: rules.custom
        }
    ];

    for (let i = 0; i < availableRules.length; ++i) {
        const ruleName = availableRules[i].name;
        if (rules[ruleName] !== undefined && !availableRules[i].test(value)) {
            return false;
        }
    }

    return true;
};

/* * * * * * * * * * * * * * * * * * * * * * * * *
 * PRICE AND DISCOUNT INCLUDING OR EXCLUDING VAT *
 * * * * * * * * * * * * * * * * * * * * * * * * */
const getUserPreferences = () => localStorage.getItem('userData') ? JSON.parse(localStorage.getItem('userData')).preferences : null;
const showIncludingVat = () => !getUserPreferences() || (getUserPreferences() && parseInt(getUserPreferences().preferredPaymentAccountId, 10) === 0 && getUserPreferences().showPricesIncludingVat);
export const priceFormat = pricesObj => showIncludingVat() ? pricesObj.priceIncludingVat : pricesObj.price;
export const discountFormat = discountObj => showIncludingVat() ? discountObj.discountIncludingVat : discountObj.discount;

/* * * * * * * * *
 * DOM UTILITIES *
 * * * * * * * * */
let scrollBarWidth = null;
export const getScrollBarWidth = () => {
    if(scrollBarWidth !== null){
        return scrollBarWidth;
    }
    const div = document.createElement('div');
    div.setAttribute('style', 'opacity: 0; visibility: hidden; overflow: scroll;');
    div.innerHTML = 'skopei';
    document.body.appendChild(div);
    scrollBarWidth = div.offsetWidth - div.clientWidth;
    document.body.removeChild(div);
    return scrollBarWidth;
};

export const getComputedStyle = (element, property, dropUnit = true) => {
    if (!element || !property) {
        return false;
    }
    const value = window.getComputedStyle(element)[property];
    const floatValue = parseFloat(value);
    return !dropUnit || isNaN(floatValue) ? value : floatValue;
};

/* * * * * * * * * * *
 * STRING UTILITIES  *
 * * * * * * * * * * */
export const translate = (key, language = 'nl', fallback) => (
    !key || !translations[language] ? '' :
        key.split('.').reduce((lookup, currentKey) => (
            typeof lookup === 'string' ? lookup : (lookup[currentKey] !== undefined ? lookup[currentKey] : (fallback || (() => {
                console.warn(`"${key}" isn't translated in "${language}"!`);
                return key;
            })()))
        ), translations[language])
);

export const localizePrice = (amount, currency, locale = 'nl-NL') => (
    window.Intl ? Intl.NumberFormat(locale, { style: 'currency', currency: (currency || 'EUR') }).format(amount) : `${{ 'EUR': '€', 'USD': '$' }[currency]} ${amount.toFixed(2)}`
);

export const createUniqueIdentifier = (prefix, length = 5, seed = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789-') => `${prefix ? prefix.toLowerCase() : ''}_${Array(Math.max(Math.min(32, length), 1)).fill().reduce((id) => `${id}${seed[Math.floor(Math.random() * seed.length)]}`, '')}`;

/* * * * * * * * * *
 * DATE UTILITIES  *
 * * * * * * * * * */
export const localizeDateTime = (dateTime, locale = 'nl-NL', opts = {}) => {
    const options = {
        // timeZone: 'Europe/Amsterdam',
        ...opts
    };
    const date = new Date(dateTime);
    const dateObject = !isNaN(date) ? date : Date.now();
    return Intl ? Intl.DateTimeFormat(locale, options).format(dateObject) : dateObject.toLocaleDateString(locale, options);
};

/* * * * * * * * * * * * * * *
 * OBJECT / ARRAY UTILITIES  *
 * * * * * * * * * * * * * * */
export const createArray = (obj) => obj ? Object.entries(obj).map(item => ({ id: item[0], config: item[1] })) : [];
export const stringToArray = (input) => (Array.isArray(input) ? input : typeof input === 'string' ? input.split(' ') : []);
export const addToArray = (newItems, currentArray) => (
    stringToArray(currentArray)
        .concat(stringToArray(newItems))
        .filter((value, index, outputArray) => value && outputArray.indexOf(value) === index)
);

/* * * * * * * * * * * * * * * * *
 * COMPONENT PROPERTY UTILITIES  *
 * * * * * * * * * * * * * * * * */
export const getStyles = (localStyles = {}) => function () {
    return [...arguments]
        .reduce((output, classNames) => (
            output.concat(stringToArray(classNames).map((className) => (localStyles[className] || globalStyles[className] || className)))
        ), [])
        .filter((className, index, output) => className && output.indexOf(className) === index)
        .join(' ').trim();
};

export const addModifierClasses = (modifiers, baseClassName, currentClasses) => {
    modifiers = stringToArray(modifiers)
        .map(mod => (baseClassName && mod) ? `${baseClassName}--${mod}` : false);
    return addToArray(modifiers, currentClasses);
};

export const getEventAttributes = (events, targetObject) => {
    const isObject = test => test && test instanceof Object && test.constructor === Object;
    events = isObject(events) ? events : {};
    targetObject = isObject(targetObject) ? targetObject : {};
    for (let event in events) {
        if (event) {
            const eventAttribute = (`on${event[0].toUpperCase()}${event.substr(1)}`);
            targetObject[eventAttribute] = events[event];
        }
    }

    return targetObject;
};

/* * * * * * * * * *
 * COLOR UTILITIES *
 * * * * * * * * * */
const textColors = { 'dark': '#383f47', 'light': '#fefefe' };
export const getTextColor = (backgroundColor) => chroma(backgroundColor).luminance() >= 0.5 ? textColors.dark : textColors.light;

const colorModifiers = {
    'color': (color) => color,
    'color-dark': (color) => chroma(color).darken().hex(),
    'color-light': (color) => chroma(color).brighten().hex(),
    'color-faded': (color) => chroma(color).alpha(0.4).css()
};
export const setBrandColors = (primaryBrandColor, secondaryBrandColor) => {
    const root = document.documentElement;
    const colorModifierNames = Object.keys(colorModifiers);

    for (let i = 0; i < colorModifierNames.length; ++i) {
        const mod = colorModifierNames[i];

        const primaryColor = colorModifiers[mod](primaryBrandColor);
        const secondaryColor = colorModifiers[mod](secondaryBrandColor);

        root.style.setProperty(`--primary-${mod}`, primaryColor);
        root.style.setProperty(`--primary-${mod}-text`, getTextColor(primaryColor));
        root.style.setProperty(`--secondary-${mod}`, secondaryColor);
        root.style.setProperty(`--secondary-${mod}-text`, getTextColor(secondaryColor));

        cssVars({
            variables: {
                [`primary-${mod}`]: primaryColor,
                [`primary-${mod}-text`]: getTextColor(primaryColor),
                [`secondary-${mod}`]: secondaryColor,
                [`secondary-${mod}-text`]: getTextColor(secondaryColor)
            }
        });
    }
};



// WEBPACK FOOTER //
// ./src/shared/utility.js